import { FormEvent, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { sendPhoneNumber } from "@/api/auth"
import { loginLogoUrl } from "@/constants/loginLogoUrl"
import {
  authErrorMessage,
  normalizePhoneForApi,
  validateBelarusMobileNineDigits,
} from "@/lib/phone"
import { loadPersonalDataConsent } from "@/lib/personalDataConsent"
import { getPrivacyDocumentRevision } from "@/lib/loadPrivacyDocument"
import PersonalDataModal from "@/pages/auth/PersonalDataModal"

const PHONE_PREFIX = "+375"

export default function LoginPage() {
  const navigate = useNavigate()
  const [phone, setPhone] = useState(PHONE_PREFIX)
  const [agreed, setAgreed] = useState(false)
  const [personalOpen, setPersonalOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    void getPrivacyDocumentRevision().then((revision) => {
      setAgreed(loadPersonalDataConsent(revision))
    })
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const nine = normalizePhoneForApi(phone.replace(/\s/g, ""))
    const phoneError = validateBelarusMobileNineDigits(nine)
    if (phoneError) {
      setError(phoneError)
      return
    }
    if (!agreed) {
      setError("Необходимо согласиться с условиями обработки данных")
      setPersonalOpen(true)
      return
    }
    setLoading(true)
    try {
      await sendPhoneNumber(nine)
      navigate("/verify", { state: { phone: nine } })
    } catch (err: unknown) {
      setError(
        authErrorMessage(
          err,
          "Не удалось отправить код. Проверьте номер и попробуйте снова."
        )
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <div className="mb-8 flex flex-col items-center text-center">
        <div className="mb-5 flex items-center justify-center rounded-[22px]">
          <img
            src={loginLogoUrl}
            alt="Daksby"
            width={160}
            height={160}
            className="h-40 w-40 max-w-[min(70vw,200px)] object-contain"
            decoding="async"
          />
        </div>
        <h1 className="text-2xl font-extrabold text-white">Вход в игру</h1>
        <p className="mt-2 max-w-xs text-sm text-guardians-muted">
          Введите номер телефона для получения SMS-кода
        </p>
      </div>

      <form className="space-y-4" onSubmit={onSubmit}>
        <label className="block text-sm text-guardians-muted">
          Телефон
          <input
            type="tel"
            autoComplete="tel"
            className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white outline-none focus:border-guardians-accent"
            value={phone}
            onChange={(ev) => setPhone(ev.target.value)}
          />
        </label>

        <button
          type="button"
          onClick={() => setPersonalOpen(true)}
          className="flex w-full cursor-pointer items-start gap-3 rounded-xl border border-transparent py-1 text-left text-sm text-guardians-muted hover:border-white/10"
        >
          <span
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
              agreed
                ? "border-guardians-accent bg-guardians-accent text-guardians-bg"
                : "border-white/25 bg-transparent"
            }`}
            aria-hidden
          >
            {agreed ? "✓" : ""}
          </span>
          <span>
            Я согласен с{" "}
            <span className="text-guardians-accent underline decoration-guardians-accent/40">
              условиями обработки персональных данных
            </span>
          </span>
        </button>

        {!agreed ? (
          <p className="text-xs leading-relaxed text-guardians-muted">
            Откроется текст политики — пролистайте до конца, затем нажмите «Я
            согласен»
          </p>
        ) : null}

        {error ? <p className="text-sm text-red-400">{error}</p> : null}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-xl bg-guardians-accent py-3 font-medium text-guardians-bg disabled:opacity-60"
        >
          {loading ? "Отправка…" : "Получить SMS-код"}
        </button>
      </form>

      <p className="mt-6 flex items-center justify-center gap-2 text-center text-xs text-guardians-muted">
        <span aria-hidden>🛡</span>
        Мы не передаём ваши данные третьим лицам
      </p>

      <PersonalDataModal
        open={personalOpen}
        onClose={() => setPersonalOpen(false)}
        onAgreed={() => setAgreed(true)}
        onDeclined={() => setAgreed(false)}
      />
    </div>
  )
}
